import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Plus, Loader2, Trash2, FileText, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import TestManagementPanel from '../../components/TestManagementPanel';

// Strip "Class " prefix to show as "2A"
const getClassDisplayName = (className) => className.replace(/^Class\s+/i, '');

// Get all section_ids for selected class_ids
// getSectionIdsForClasses - removed, using classes directly
// 

const TTSModulePage = ({ embedded = false }) => {
  const [tests, setTests] = useState([]);
  const [classes, setClasses] = useState([]);
  // Sections removed - using classes directly
  const [loading, setLoading] = useState(true);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [testFormData, setTestFormData] = useState({
    test_name: '',
    class_ids: [],
    import_text: ''
  });
  const [importClassId, setImportClassId] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncState, setSyncState] = useState(null); // null | {status, jobId, done, total, errors, percent, message, speaker}
  const syncIntervalRef = React.useRef(null);

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    return () => { if (syncIntervalRef.current) clearInterval(syncIntervalRef.current); };
  }, []);

      const fetchData = async () => {
    try {
      const [testsRes, classesRes] = await Promise.all([
        api.get('/tests?module_type=tts'),
        api.get('/classes')
      ]);
      setTests(testsRes.data);
      setClasses(classesRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTest = async (e) => {
    e.preventDefault();
    if (!testFormData.test_name || testFormData.class_ids.length === 0 || !testFormData.import_text) {
      toast.error('Please fill test name, classes, and sentences');
      return;
    }

    // Use first selected class if importClassId not specified
    const questionClassId = importClassId || testFormData.class_ids[0];

    setSaving(true);
    try {
      const lines = testFormData.import_text.split('\n').filter(line => line.trim() !== '');
      const questionIds = [];
      for (const line of lines) {
        const response = await api.post('/questions', {
          question_text: line.trim(),
          class_id: questionClassId,
          module_type: 'tts'
        });
        questionIds.push(response.data.id || response.data);
      }

      const response = await api.post('/tests', {
        test_name: testFormData.test_name,
        module_type: 'tts',
        section_ids: testFormData.class_ids,  // Direct class assignment
        question_ids: questionIds
      });

      toast.success(`${response.data.test_number} created with ${questionIds.length} questions`);
      resetForm();
      setTestDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create test');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTest = async (e) => {
    e.preventDefault();
    if (!editingTest) return;
    if (!testFormData.test_name || testFormData.class_ids.length === 0) {
      toast.error('Please fill test name and select at least one class');
      return;
    }

    setSaving(true);
    try {
      let questionIds = editingTest.questions.map(q => q.id);
      if (testFormData.import_text) {
        const questionClassId = importClassId || testFormData.class_ids[0];
        const lines = testFormData.import_text.split('\n').filter(line => line.trim() !== '');
        for (const line of lines) {
          const response = await api.post('/questions', {
            question_text: line.trim(),
            class_id: questionClassId,
            module_type: 'tts'
          });
          questionIds.push(response.data.id || response.data);
        }
      }

      // using class_ids directly
      await api.put(`/tests/${editingTest.id}`, {
        test_name: testFormData.test_name,
        section_ids: testFormData.class_ids, // Direct class assignment
        question_ids: questionIds
      });

      toast.success('Test updated successfully');
      resetForm();
      setTestDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update test');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setTestFormData({ test_name: '', class_ids: [], import_text: '' });
    setImportClassId('');
    setEditingTest(null);
  };

  const handleDeleteTest = async (testId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this test?')) return;
    try {
      await api.delete(`/tests/${testId}`);
      toast.success('Test deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete test');
    }
  };

  const handleDeleteQuestion = async (testId, questionId, e) => {
    e.stopPropagation();
    if (!window.confirm('Remove this question from the test?')) return;
    try {
      const test = await api.get(`/tests/${testId}`);
      const updatedIds = test.data.questions.filter(q => q.id !== questionId).map(q => q.id);
      await api.put(`/tests/${testId}`, { question_ids: updatedIds });
      toast.success('Question removed');
      openTestDialog(testId);
      fetchData();
    } catch (err) {
      toast.error('Failed to remove question');
    }
  };

  const openTestDialog = async (testId) => {
    try {
      const response = await api.get(`/tests/${testId}`);
      const t = response.data;
      setEditingTest(t);
      // Map assigned classes back to class_ids
      const classIds = (t.classes || []).map(c => c.id);
      setTestFormData({
        test_name: t.test_name,
        class_ids: classIds,
        import_text: ''
      });
      setImportClassId('');
      setTestDialogOpen(true);
    } catch (err) {
      toast.error('Failed to load test details');
    }
  };

  const handleExportTest = async (testId, e) => {
    e.stopPropagation();
    try {
      const response = await api.get(`/tests/${testId}`);
      const t = response.data;
      let txt = `Test: ${t.test_name}\nModule: TTS\nCreated: ${new Date(t.created_at).toLocaleDateString()}\n\n--- Sentences ---\n\n`;
      t.questions.forEach((q, i) => { txt += `${i + 1}. ${q.question_text}\n\n`; });
      const blob = new Blob([txt], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${t.test_name.replace(/\s+/g, '_')}_TTS.txt`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success('Exported');
    } catch (err) { toast.error('Export failed'); }
  };

  const toggleClassSelection = (classId) => {
    setTestFormData(prev => ({
      ...prev,
      class_ids: prev.class_ids.includes(classId)
        ? prev.class_ids.filter(id => id !== classId)
        : [...prev.class_ids, classId]
    }));
  };

  const startSync = async () => {
    setSyncState({ status: 'starting', message: 'Checking audio files...' });
    try {
      const res = await api.post('/tts/sync-module', { module_type: 'tts' });
      const data = res.data;
      if (data.status === 'already_complete') {
        setSyncState({ status: 'complete', message: `All ${data.total} audio files are up to date.`, percent: 100 });
      } else if (data.status === 'no_questions') {
        setSyncState({ status: 'complete', message: 'No questions found for your classes.', percent: 100 });
      } else {
        // started
        setSyncState({ status: 'running', jobId: data.job_id, done: 0, total: data.total, errors: 0, percent: 0, speaker: data.speaker, alreadyCached: data.already_cached, grandTotal: data.grand_total, message: `Generating ${data.total} audio files...` });
        syncIntervalRef.current = setInterval(async () => {
          try {
            const jr = await api.get(`/tts/sync-job/${data.job_id}`);
            const j = jr.data;
            setSyncState(prev => ({ ...prev, done: j.done, errors: j.errors, percent: j.percent, status: j.status === 'complete' ? 'complete' : 'running', message: j.status === 'complete' ? `Done! Generated ${j.done} files${j.errors ? `, ${j.errors} errors` : ''}.` : `Generating audio... ${j.done}/${prev.total}` }));
            if (j.status === 'complete') {
              clearInterval(syncIntervalRef.current);
              syncIntervalRef.current = null;
            }
          } catch { /* ignore poll errors */ }
        }, 2000);
      }
    } catch (err) {
      setSyncState({ status: 'error', message: err.response?.data?.detail || 'Sync failed' });
    }
  };

  const getTestClassesDisplay = (test) => {
    if (!test.classes?.length) return 'No classes';
    // Map section class_names to display
    return test.classes.map(s => getClassDisplayName(s.class_name || s.name)).join(', ');
  };

  const content = (
    <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Text to Speech</h1>
            <p className="text-slate-500">Create and manage TTS tests with sentence practice</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={startSync} disabled={syncState?.status === 'running' || syncState?.status === 'starting'}>
              <RefreshCw className={`h-4 w-4 mr-2 ${syncState?.status === 'running' || syncState?.status === 'starting' ? 'animate-spin' : ''}`} />
              Sync Audio
            </Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { resetForm(); setTestDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />Create Test
            </Button>
          </div>
        </div>

        {syncState && (
          <div className={`rounded-lg border p-4 ${syncState.status === 'complete' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : syncState.status === 'error' ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {syncState.status === 'complete' ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : syncState.status === 'error' ? <AlertCircle className="h-4 w-4 text-red-600" /> : <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
                <span className="text-sm font-medium">{syncState.message}</span>
              </div>
              {(syncState.status === 'complete' || syncState.status === 'error') && (
                <button onClick={() => setSyncState(null)} className="text-xs text-slate-500 hover:text-slate-700">Dismiss</button>
              )}
            </div>
            {(syncState.status === 'running' || syncState.status === 'complete') && syncState.total > 0 && (
              <div className="space-y-1">
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div className="bg-indigo-600 h-2 rounded-full transition-all duration-300" style={{ width: `${syncState.percent || 0}%` }} />
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>{syncState.done || 0} / {syncState.total} generated</span>
                  {syncState.alreadyCached > 0 && <span>{syncState.alreadyCached} already cached</span>}
                  {syncState.errors > 0 && <span className="text-red-500">{syncState.errors} errors</span>}
                </div>
              </div>
            )}
          </div>
        )}

        <Dialog open={testDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setTestDialogOpen(open); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTest ? `Edit: ${editingTest.test_number}` : 'Create New TTS Test'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={editingTest ? handleUpdateTest : handleCreateTest} className="space-y-4">
              <div className="space-y-2">
                <Label>Test Name / Topic *</Label>
                <Input value={testFormData.test_name}
                  onChange={(e) => setTestFormData({ ...testFormData, test_name: e.target.value })}
                  placeholder="e.g., Reading Practice - Paragraph 1" />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Assign to Classes *</Label>
                  <button type="button" onClick={() => setTestFormData(prev => ({ ...prev, class_ids: prev.class_ids.length === classes.length ? [] : classes.map(c => c.id) }))} className="text-xs text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 font-medium underline-offset-2 hover:underline">
                    {testFormData.class_ids.length === classes.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="border rounded-lg p-4 max-h-52 overflow-y-auto">
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {classes.map(cls => {
                      const displayName = getClassDisplayName(cls.name);
                      const isSelected = testFormData.class_ids.includes(cls.id);
                      return (
                        <label key={cls.id} className={`flex items-center justify-center p-2 rounded-lg border-2 cursor-pointer transition-all text-sm font-semibold ${isSelected ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300'}`}>
                          <input type="checkbox" className="sr-only" checked={isSelected} onChange={() => toggleClassSelection(cls.id)} />
                          {displayName}
                        </label>
                      );
                    })}
                  </div>
                </div>
                <p className="text-xs text-slate-500">{testFormData.class_ids.length} class(es) selected</p>
              </div>

              {editingTest && editingTest.questions?.length > 0 && (
                <div className="space-y-2">
                  <Label>Current Sentences ({editingTest.questions.length})</Label>
                  <div className="border rounded-lg p-4 max-h-48 overflow-y-auto bg-slate-50 dark:bg-slate-900">
                    {editingTest.questions.map((q, i) => (
                      <div key={q.id} className="flex items-start gap-2 mb-2 p-2 bg-white dark:bg-slate-800 rounded">
                        <span className="text-sm flex-1"><span className="font-medium">#{i + 1}:</span> {q.question_text}</span>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-rose-600"
                          onClick={(e) => handleDeleteQuestion(editingTest.id, q.id, e)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Import Sentences Class - HIDDEN (not required, auto-uses first selected class) */}
              <div className="hidden">
                <Label>{editingTest ? 'Add More Sentences' : 'Import Sentences'}</Label>
                <Select value={importClassId} onValueChange={setImportClassId}>
                  <SelectTrigger><SelectValue placeholder="Select class for questions" /></SelectTrigger>
                  <SelectContent>
                    {classes.map((cls) => (
                      <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Sentences (one per line) {!editingTest && '*'}</Label>
                <Textarea value={testFormData.import_text}
                  onChange={(e) => setTestFormData({ ...testFormData, import_text: e.target.value })}
                  placeholder="Enter sentences, one per line:&#10;The cat sat on the mat.&#10;A quick brown fox jumps over the lazy dog." rows={8} />
                <p className="text-xs text-slate-500">{testFormData.import_text.split('\n').filter(l => l.trim()).length} sentence(s) to import</p>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { resetForm(); setTestDialogOpen(false); }}>Cancel</Button>
                <Button type="submit" disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingTest ? 'Update Test' : 'Create Test'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
        ) : tests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No tests created yet</p>
            </CardContent>
          </Card>
        ) : (
          <TestManagementPanel
            tests={tests}
            classes={classes}
            onRefresh={fetchData}
            moduleColor="sky"
            moduleType="tts"
          />
        )}
      </div>
  );
  if (embedded) return content;
  return <DashboardLayout>{content}</DashboardLayout>;
};

export default TTSModulePage;
